<template>
  <div class="team-members-table">
    <table class="team-members-table__table">
      <thead class="team-members-table__header">
        <tr>
          <th class="team-members-table__th">Name</th>
          <th class="team-members-table__th">Date requested</th>
          <th class="team-members-table__th">Email address</th>
          <th class="team-members-table__th">Role</th>
          <th class="team-members-table__th team-members-table__th--actions">Actions</th>
        </tr>
      </thead>
      <tbody class="team-members-table__body">
        <tr v-for="member in members" :key="member.id" class="team-members-table__row">
          <td class="team-members-table__td">
            <span class="team-members-table__name">{{ member.fname }} {{ member.lname }}</span>
          </td>
          <td class="team-members-table__td">
            {{ formatDate(member.createdAt) }}
          </td>
          <td class="team-members-table__td">
            {{ member.email }}
          </td>
          <td class="team-members-table__td">
            <span class="team-members-table__role">{{ member.groups[0]?.name ?? "No role" }}</span>
          </td>
          <td class="team-members-table__td team-members-table__td--actions">
            <Popper offsetDistance="5">
              <button class="team-members-table__action-trigger" :disabled="!isActionAllowed">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                </svg>
              </button>
              <template #content>
                <div class="team-members-table__dropdown">
                  <ul class="team-members-table__dropdown-list">
                    <li>
                      <button
                        class="team-members-table__dropdown-item team-members-table__dropdown-item--danger"
                        :disabled="!isActionAllowed"
                      >
                        Remove Member
                      </button>
                    </li>
                  </ul>
                </div>
              </template>
            </Popper>
          </td>
        </tr>
        <tr v-if="members.length === 0 && !loading">
          <td colspan="5" class="team-members-table__empty">
            No team members found matching your search.
          </td>
        </tr>
        <tr v-if="loading">
          <td colspan="5" class="team-members-table__loading">
            <svg class="team-members-table__spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="team-members-table__spinner-track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="team-members-table__spinner-head" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
    
    <script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Popper from "vue3-popper";

export default {
  props: {
    members: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isActionAllowed: Boolean,
  },

  emits: ["edit-role", "delete-role"],

  components: {
    Popper,
  },

  setup(props, { emit }) {
    const searchQuery = ref("");
    const moreActionsId = ref(null);

    function formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString();
    }

    function handleMoreActions(role) {
      moreActionsId.value = moreActionsId.value === role.id ? null : role.id;
    }

    function editRole(roleId) {
      moreActionsId.value = null;
      emit("edit-role", roleId);
    }

    function deleteRole(roleId) {
      moreActionsId.value = null;
      emit("delete-role", roleId);
    }

    const handleOutsideClick = (event) => {
      if (moreActionsId.value !== null && !event.target.closest(".actions")) {
        moreActionsId.value = null;
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleOutsideClick);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleOutsideClick);
    });

    watch(
      () => props.members,
      () => {
        moreActionsId.value = null;
      }
    );

    return {
      moreActionsId,
      formatDate,
      handleMoreActions,
      editRole,
      deleteRole,
    };
  },
};
</script>
  
 
<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-subtle: #F8FAFC;
$bg-hover: #F1F5F9;
$border-color: #E2E8F0;
$error: #DC2626;
$error-light: #FEF2F2;

.team-members-table {
  width: 100%;
  overflow-x: auto;

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__th {
    padding: 16px 24px;
    text-align: left;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: $text-muted;
    background: $bg-subtle;
    border-bottom: 1px solid $border-color;

    &--actions {
      text-align: right;
    }
  }

  &__row {
    transition: background 0.15s ease;

    &:hover {
      background: $bg-hover;
    }
  }

  &__td {
    padding: 20px 24px;
    font-size: 14px;
    color: $text-secondary;
    border-bottom: 1px solid $border-color;

    &--actions {
      text-align: right;
    }
  }

  &__name {
    font-weight: 500;
    color: $text-primary;
  }

  &__role {
    display: inline-block;
    padding: 4px 10px;
    background: $bg-subtle;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    color: $text-secondary;
  }

  &__action-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: transparent;
    border: 1px solid transparent;
    color: $text-muted;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: $bg-subtle;
      border-color: $border-color;
      color: $text-primary;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__dropdown {
    min-width: 180px;
    padding: 8px;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px solid $border-color;
  }

  &__dropdown-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__dropdown-item {
    display: block;
    width: 100%;
    padding: 12px 14px;
    font-size: 14px;
    font-weight: 450;
    color: $text-secondary;
    background: none;
    border: none;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: $bg-subtle;
      color: $text-primary;
    }

    &--danger {
      color: $error;

      &:hover {
        background: $error-light;
        color: $error;
      }
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__empty {
    padding: 40px 24px;
    text-align: center;
    color: $text-muted;
    font-size: 14px;
  }

  &__loading {
    padding: 40px 24px;
    text-align: center;
  }

  &__spinner {
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
    margin: 0 auto;
    display: block;
  }

  &__spinner-track {
    opacity: 0.25;
  }

  &__spinner-head {
    opacity: 0.75;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}
</style>